import { ExclamationTriangleIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';

import type { RBACUser, RoleMember } from '../../../types';

type Props = {
  user: RBACUser | RoleMember;
  deleted?: boolean;
  added?: boolean;
  onDelete?: () => void;
};

// A member/owner row: name + UPN, mirroring the UserAutocomplete option style, with
// the same green-add / red-delete staging colours as GroupDisplayItem. Surfaces a
// disabled-account warning and flags the rare nested-group member (see RoleMember).
export default function UserDisplayItem({ user, deleted = false, added = false, onDelete }: Props) {
  const isGroup = 'isGroup' in user && user.isGroup;

  const className = classNames(
    { 'text-error-content bg-error': deleted },
    { 'text-success-content bg-success': added },
    'p-2',
    'flex',
    'justify-between',
    'items-center'
  );

  return (
    <li className={className}>
      <div className="min-w-0">
        <div className="flex flex-row items-center gap-1">
          <div className="truncate">{user.displayName}</div>
          {!user.accountEnabled && !isGroup && (
            <ExclamationTriangleIcon
              className="h-4 w-4 shrink-0 text-warning"
              title="User is disabled"
            />
          )}
        </div>
        {isGroup ? (
          <div className="text-xs opacity-70 flex flex-row items-center gap-1">
            <UserGroupIcon className="h-3 w-3 shrink-0" />
            nested group
          </div>
        ) : (
          <div className="text-xs opacity-70 truncate" title={user.userPrincipalName}>
            {user.userPrincipalName}
          </div>
        )}
      </div>

      {onDelete && (
        <div>
          <button
            className="btn btn-ghost btn-xs btn-circle"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </li>
  );
}
